import { useState, useEffect } from 'react';
import type MarkdownIt from 'markdown-it';

import { sanitize } from '../../../utils/sanitize';

type MarkdownRenderer = MarkdownIt | null;

// Кэшируем рендерер после первой загрузки — он создаётся один раз для всего приложения
let cachedRenderer: MarkdownRenderer = null;

/**
 * Хук для преобразования markdown-текста в безопасный HTML.
 * Использует динамический импорт для markdown-it и katex, чтобы вынести их
 * из main бандла в отдельный чанк (~700 KB), загружаемый только при рендере Markdown.
 *
 * @param content - Исходный markdown-текст
 * @returns Безопасный HTML-контент (пустая строка до загрузки рендерера)
 */
export const useMarkdownContent = (content: string): string => {
    const [renderer, setRenderer] = useState<MarkdownRenderer>(cachedRenderer);

    useEffect(() => {
        if (cachedRenderer) {
            setRenderer(cachedRenderer);

            return;
        }

        // Динамически загружаем тяжёлые зависимости (markdown-it + katex + markdown-it-texmath)
        import('./Markdown.helpers').then(({ createMarkdownRenderer }) => {
            cachedRenderer = createMarkdownRenderer();
            setRenderer(cachedRenderer);
        });
    }, []);

    if (!renderer) {
        return '';
    }

    return sanitize(renderer.render(content));
};
